import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePortalOverview } from './usePortalOverview';
import { updatePortalAbout, type AboutProfile } from '@/lib/api/endpoints/portal';
import { formatSchedule } from './portalUtils';
import './portal.css';

/** The full AI-generated profile (left column) — read view of the extracted data. */
function GeneratedProfile({ profile, objectives }: { profile: AboutProfile; objectives: string }) {
  const empty = !profile.summary && profile.age === undefined && !(profile.injuries?.length)
    && !(profile.limitations?.length) && !(profile.preferences?.length) && !profile.notes;
  if (empty && !objectives) {
    return <p className="portal-muted">Your profile will appear here once you tell us about yourself in the box. →</p>;
  }
  return (
    <div className="portal-genprofile">
      {profile.summary && <p className="portal-gp-summary">{profile.summary}</p>}
      {profile.age !== undefined && (
        <div className="portal-gp-field"><span className="portal-gp-k">Age</span><span className="portal-gp-v">{profile.age}</span></div>
      )}
      {objectives && (
        <div className="portal-gp-block">
          <div className="portal-gp-label">Goals</div>
          <p className="portal-gp-text">{objectives}</p>
        </div>
      )}
      {!!profile.injuries?.length && (
        <div className="portal-gp-block">
          <div className="portal-gp-label">Injuries</div>
          <div className="portal-chips">{profile.injuries.map((x, i) => <span key={i} className="portal-chip">{x}</span>)}</div>
        </div>
      )}
      {!!profile.limitations?.length && (
        <div className="portal-gp-block">
          <div className="portal-gp-label">Limitations</div>
          <div className="portal-chips">{profile.limitations.map((x, i) => <span key={i} className="portal-chip">{x}</span>)}</div>
        </div>
      )}
      {!!profile.preferences?.length && (
        <div className="portal-gp-block">
          <div className="portal-gp-label">Preferences</div>
          <div className="portal-chips">{profile.preferences.map((x, i) => <span key={i} className="portal-chip">{x}</span>)}</div>
        </div>
      )}
      {profile.notes && (
        <div className="portal-gp-block">
          <div className="portal-gp-label">Notes</div>
          <p className="portal-gp-text">{profile.notes}</p>
        </div>
      )}
    </div>
  );
}

export function ProfileView() {
  const { data, isLoading, isError, token, workspaceId } = usePortalOverview();
  const queryClient = useQueryClient();

  const [aboutText, setAboutText] = useState('');
  const [savingAbout, setSavingAbout] = useState(false);
  const [profile, setProfile] = useState<AboutProfile>({});

  useEffect(() => {
    if (data) {
      setAboutText(data.aboutText);
      setProfile(data.aboutProfile);
    }
  }, [data]);

  if (isLoading) return <div className="portal-page"><div className="portal-spinner">Loading…</div></div>;
  if (isError || !data) return <div className="portal-page"><div className="portal-error">Unable to load your profile.</div></div>;

  const saveAbout = async () => {
    if (!token) return;
    setSavingAbout(true);
    try {
      const res = await updatePortalAbout(token, workspaceId, aboutText);
      setProfile(res.aboutProfile);
      await queryClient.invalidateQueries({ queryKey: ['portal-overview', workspaceId] });
    } finally {
      setSavingAbout(false);
    }
  };

  const aboutDirty = aboutText !== data.aboutText;

  return (
    <div className="portal-page portal-profile">
      <div className="portal-page-header"><h1>Profile</h1></div>

      <div className="portal-profile-grid">
        {/* Left — the full AI-generated profile */}
        <div className="portal-section">
          <div className="portal-section-head">
            <h2>About Me</h2>
            <span className="portal-inbody-tag">AI generated</span>
          </div>
          <GeneratedProfile profile={profile} objectives={data.objectives} />
        </div>

        {/* Right — update box (regenerates the profile via AI) + goals */}
        <aside className="portal-profile-side">
          <div className="portal-section">
            <h2>Update your profile</h2>
            <p className="portal-muted" style={{ marginTop: 0 }}>
              Describe yourself — age, injuries, anything Mariana should know. We’ll rebuild your profile from it.
            </p>
            <textarea
              className="portal-textarea"
              rows={6}
              placeholder="e.g. 41, tore my left ACL skiing in 2022, no deep squats, prefer mornings"
              value={aboutText}
              onChange={(e) => setAboutText(e.target.value)}
            />
            <button className="portal-btn" style={{ marginTop: 10 }} onClick={saveAbout} disabled={savingAbout || !aboutDirty}>
              {savingAbout ? 'Updating…' : 'Update with AI'}
            </button>
          </div>

          <div className="portal-section">
            <h2>Session schedule</h2>
            {data.schedule.length > 0 ? (
              <div className="portal-schedule">
                <span className="portal-schedule-when">{formatSchedule(data.schedule)}</span>
                {data.trainer.name && <span className="portal-schedule-who">with {data.trainer.name.split(/\s+/)[0]}</span>}
              </div>
            ) : (
              <p className="portal-muted">No recurring sessions set yet.</p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
